<script setup lang="ts">
import type { TrekRouteVariant } from "@/composables/useTrekPackageSummary";

const props = withDefaults(
  defineProps<{
    trekTitle: string;
    /** Route variant determines which package (Full / Westerly / Easterly) is shown. */
    variant?: TrekRouteVariant;
    /** Only one strip on the page should enable the sticky CTA bar. */
    sticky?: boolean;
  }>(),
  {
    variant: "full",
    sticky: false,
  },
);

const { summaryForVariant } = useTrekPackageSummary(() => props.trekTitle);

const summary = computed(() => summaryForVariant(props.variant));

const showEnquiryModal = ref(false);
const stripEl = ref<HTMLElement | null>(null);
const packagesInView = ref(false);
const stripPassed = ref(false);

const showSticky = computed(
  () =>
    props.sticky &&
    !!summary.value &&
    stripPassed.value &&
    !packagesInView.value &&
    !showEnquiryModal.value,
);

const availabilityClass = computed(() =>
  summary.value?.availability
    ? summary.value.availability.toLowerCase().replace(/\s+/g, "")
    : "",
);

function scrollToPackages() {
  const el = document.getElementById("trekPackages");
  el?.scrollIntoView({ behavior: "smooth", block: "start" });
}

const { requestOpenPackageModal } = useOpenTrekPackageModal();

function checkDates() {
  const pkg = summary.value?.package;
  if (!pkg) {
    scrollToPackages();
    return;
  }
  requestOpenPackageModal({
    packageId: pkg.id,
    packageTitle: pkg.trek_title,
    preferYear2: true,
  });
}

function openEnquiry() {
  showEnquiryModal.value = true;
}

function closeEnquiry() {
  showEnquiryModal.value = false;
}

let stripObserver: IntersectionObserver | null = null;
let packagesObserver: IntersectionObserver | null = null;

onMounted(() => {
  if (!props.sticky || typeof IntersectionObserver === "undefined") return;

  if (stripEl.value) {
    stripObserver = new IntersectionObserver(
      ([entry]) => {
        stripPassed.value =
          !entry?.isIntersecting && (entry?.boundingClientRect.top ?? 0) < 0;
      },
      { threshold: 0, rootMargin: "-80px 0px 0px 0px" },
    );
    stripObserver.observe(stripEl.value);
  }

  const packagesEl = document.getElementById("trekPackages");
  if (packagesEl) {
    packagesObserver = new IntersectionObserver(
      ([entry]) => {
        packagesInView.value = !!entry?.isIntersecting;
      },
      { threshold: 0.15 },
    );
    packagesObserver.observe(packagesEl);
  }
});

onUnmounted(() => {
  stripObserver?.disconnect();
  packagesObserver?.disconnect();
});
</script>

<template>
  <section
    v-if="summary"
    ref="stripEl"
    class="map-availability-strip"
    aria-label="Trek availability"
  >
    <div class="map-availability-strip__main">
      <div class="map-availability-strip__copy">
        <p class="map-availability-strip__eyebrow">Plan your trek</p>
        <h3 class="map-availability-strip__title">
          {{ summary.package.trek_title }}
        </h3>
        <p v-if="summary.yearLabel" class="map-availability-strip__year">
          {{ summary.yearLabel }} season
        </p>
      </div>

      <div class="map-availability-strip__meta">
        <div
          v-if="summary.availability"
          class="map-availability-strip__badge"
          :class="availabilityClass"
        >
          {{ summary.availability }}
        </div>
        <div v-if="summary.fromPrice" class="map-availability-strip__price">
          <span class="from">From</span>
          <span class="amount">&pound;{{ summary.fromPrice }}</span>
          <span v-if="summary.personDay" class="per">{{
            summary.personDay
          }}</span>
        </div>
        <div v-if="summary.nextDate" class="map-availability-strip__next">
          <strong>Next dates:</strong> {{ summary.nextDate }}
          <span v-if="summary.spacesLabel"> · {{ summary.spacesLabel }}</span>
        </div>
      </div>

      <div class="map-availability-strip__actions">
        <button type="button" class="btn from-left" @click="checkDates">
          Check dates
        </button>
        <button
          type="button"
          class="btn from-left enquire-btn"
          @click="openEnquiry"
        >
          Enquire
        </button>
      </div>
    </div>
  </section>

  <Teleport to="body">
    <Transition name="slideup">
      <div
        v-if="showSticky && summary"
        class="map-availability-sticky"
        role="region"
        aria-label="Quick enquire"
      >
        <div class="map-availability-sticky__inner">
          <div class="map-availability-sticky__text">
            <span
              v-if="summary.availability"
              class="map-availability-strip__badge"
              :class="availabilityClass"
              >{{ summary.availability }}</span
            >
            <span v-if="summary.nextDate">{{ summary.nextDate }}</span>
            <span v-if="summary.fromPrice"
              >From &pound;{{ summary.fromPrice }}</span
            >
          </div>
          <div class="map-availability-sticky__actions">
            <button type="button" class="btn from-left" @click="checkDates">
              Dates
            </button>
            <button
              type="button"
              class="btn from-left enquire-btn"
              @click="openEnquiry"
            >
              Enquire
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>

  <Teleport to="body">
    <Transition name="pop" appear>
      <div
        v-if="showEnquiryModal && summary"
        v-cloak
        class="modal trek-enquiry-modal"
      >
        <button
          class="close-modal"
          aria-label="Close"
          @click="closeEnquiry"
        />
        <div class="modal-inner">
          <AppFormTrekEnquiryForm
            :trek-route="summary.package.trek_route ?? trekTitle"
            :trek-title="summary.package.trek_title ?? ''"
            :trek-price="summary.fromPrice"
            :trek-person-day="summary.personDay"
            :trek-dates="summary.nextDate"
            :places-available="summary.spacesLabel"
            :availability="summary.availability"
            @close="closeEnquiry"
            @success="closeEnquiry"
          />
        </div>
      </div>
    </Transition>
    <Transition name="fade" appear>
      <div
        v-if="showEnquiryModal"
        v-cloak
        class="backdrop"
        @click="closeEnquiry"
      />
    </Transition>
  </Teleport>
</template>
